<template>
  <div>
    <v-row>
      <v-col class="noYPadding">
        <VSelect
          v-model="data.dataSource"
          hideDetails
          outlined
          :disabled="editMode"
          data-cy="flowMeter-channel"
          :label="$t('devices.flowMeters.sources.label')"
          :rules="{ required: true }"
          :items="sources"
          itemValue="value"
          itemText="name"
          @input="updateData"
        />
      </v-col>
    </v-row>

    <!-- This VSelect will only be displayed if meter.dataSource is 'IOT' -->
    <v-row v-if="data.dataSource === SOURCE_IOT">
      <v-col class="noYPadding">
        <!-- Assuming you have another VSelect to show -->
        <VSelect
          v-model="data.flowMeterHub"
          hideDetails
          outlined
          data-cy="flowMeter-unitOfMeasure"
          :label="$t('devices.flowMetersHubs.name')"
          :rules="{ required: true }"
          :items="availableMetersHubs"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <!-- This VSelect will only be displayed if meter.dataSource is 'IOT' AND the flow meter hub is selected  -->
    <v-row v-if="data.dataSource === SOURCE_IOT && data.flowMeterHub">
      <v-col class="noYPadding">
        <!-- Assuming you have another VSelect to show -->
        <VSelect
          v-model="data.flowMeter"
          hideDetails
          outlined
          data-cy="flowMeter-unitOfMeasure"
          :label="$t('devices.flowMeters.name')"
          :rules="{ required: true }"
          :items="availableMeters"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <!-- This VSelect will only be displayed if meter.dataSource is 'CONTROLLER'  -->
    <v-row v-if="data.dataSource === SOURCE_CONTROLLER">
      <v-col class="noYPadding">
        <VSelect
          v-model="data.controller"
          hideDetails
          outlined
          :disabled="editMode"
          data-cy="flowMeter-controllerHub"
          :label="$t('devices.controllersHubs.name')"
          :rules="{ required: true }"
          :items="availableControllersHubs"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <!-- This VSelect will only be displayed if meter.dataSource is 'CONTROLLER' AND the controller hub is selected  -->
    <v-row v-if="data.dataSource === SOURCE_CONTROLLER && data.controller && !editMode">
      <v-col class="noYPadding">
        <VSelect
          v-model="data.probe"
          hideDetails
          outlined
          :disabled="editMode"
          data-cy="flowMeter-probe"
          :label="$t('devices.probes.name')"
          :rules="{ required: true }"
          :items="availableProbes"
          @input="updateData"
        />
      </v-col>
    </v-row>

    <v-row v-if="data.dataSource === SOURCE_CONTROLLER && data.controller && data.controller.length > 0 && editMode">
      <v-col class="noYPadding">
        <VTextField
          v-model="data.name"
          :label="$t('devices.probes.name')"
          hideDetails
          outlined
          disabled
        />
      </v-col>
    </v-row>

    <!-- This VSelect will only be displayed if meter.dataSource is 'CONTROLLER' AND the controller hub is selected  -->
    <v-row v-if="data.dataSource === SOURCE_CONTROLLER && data.controller && data.probe">
      <v-col class="noYPadding">
        <VSelect
          v-model="data.unitsOfMeasure"
          hideDetails
          outlined
          data-cy="flowMeter-unitOfMeasure"
          :label="$t('devices.measureUnit')"
          :rules="{ required: true }"
          :items="genericVolumes"
          @input="updateData"
        />
      </v-col>
    </v-row>

    <!-- This TextField will only be displayed if meter.dataSource is 'MANUAL' -->
    <v-row v-if="data.dataSource === SOURCE_MANUAL">
      <v-col class="noYPadding">
        <v-row>
          <v-col class="noYPadding">
            <VTextField
              v-model="data.name"
              hideDetails
              outlined
              data-cy="flowMeter-Name"
              :label="$t('forms.labels.name')"
              :rules="{ required: true }"
              @input="updateData"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col class="noYPadding">
            <VSelect
              v-model="data.unitsOfMeasure"
              hideDetails
              outlined
              data-cy="flowMeter-unitOfMeasure"
              :label="$t('devices.measureUnit')"
              :rules="{ required: true }"
              :items="genericVolumes"
              @input="updateData"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- resetAt -->
    <v-row v-if="data.dataSource === SOURCE_IOT || data.dataSource === SOURCE_CONTROLLER">
      <v-col class="noYPadding">
        <!-- Assuming you have another VSelect to show -->
        <VTextField
          v-model="data.resetAt"
          hideDetails
          outlined
          type="number"
          data-cy="flowMeter-resetAt"
          :rules="{ required: true, min: 1 }"
          :disabled="data.dataSource === SOURCE_IOT || editMode"
          :label="$t('devices.flowMeters.resetAt')"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <v-row v-if="flowmeterType">
      <v-col class="noYPadding">
        <VSelect
          v-model="data.type"
          outlined
          disabled
          hideDetails
          data-cy="flowMeter-channel"
          :label="$t('devices.flowMeters.types.label')"
          :items="flowmeterTypes"
          itemText="name"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import LinkMeterMixin from '@/mixins/LinkMeterMixin';
import { genericVolumes } from '@/aquassist-shared/constants/volumes';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import { SOURCE_MANUAL, SOURCE_CONTROLLER, SOURCE_IOT, TYPE_MAKE_UP, TYPE_BLOWDOWN, TYPE_FEED, TYPE_STEAM, TYPE_CONDENSATE } from '@/aquassist-shared/constants/types/flowMeters';
import { mapGetters } from 'vuex';

export default {
    name: 'LinkFlowMeterForm',
    components: {
        VSelect,
        VTextField,
    },
    mixins: [
        LinkMeterMixin,
    ],
    props: {
        flowmeterType: {
            type: String,
            default: '',
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        meter: {
            type: Object,
            default: () => ({}),
        },
        typeOfComponent: {
            type: String,
            default: null,
            required: false,
        },
    },
    data() {
        return {
            data: {
                name: '',
                channel: '',
                unitsOfMeasure: '',
                dataSource: '',
                resetAt: 0,
                flowMeterHub: null,
                flowMeter: null,
                controller: null,
                probe: null,
                type: this.flowmeterType || '',
            },
            availableMeters: [],
            availableMetersHubs: [],
            availableControllersHubs: [],
            availableProbes: [],
            componentInitialized: false,
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'site',
        ]),
        genericVolumes() {
            return genericVolumes.filter(v => v.tag.includes('flowMeters')).map(p => ({
                name: this.$t(`devices.productUnitMeasure.${p.name.toLowerCase()}`),
                id: p.name,
            }));
        },
        flowmeterTypes() {
            return [TYPE_MAKE_UP, TYPE_BLOWDOWN, TYPE_FEED, TYPE_STEAM, TYPE_CONDENSATE].map(type => ({
                name: this.$t(`devices.flowMeters.types.${type}`),
                id: type,
            }));
        },
    },
    watch: {
        // Watch for changes in the selected hub
        'data.flowMeterHub': {
            immediate: false,
            async handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    await this.getAvailableMeters(this.availableMetersHubs, this.data.flowMeterHub);
                }
            },
        },
        'data.controller': {
            immediate: false,
            async handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    await this.getAvailableProbes();
                }
            },
        },
        'data.probe': {
            immediate: true,
            async handler(newVal, oldVal) {
                if (newVal !== oldVal && this.componentInitialized) {
                    this.getCustomName();
                }
            },
        },
    },
    async created() {
        if (this.editMode && this.data.dataSource !== SOURCE_IOT) {
            const flowMeterName = this.meter?.flowMeter?.name;
            const probe = this.meter?.controller?.probes.find(p => p.probeId === flowMeterName || p.name === flowMeterName);
            this.data = {
                ...this.meter.flowMeter,
                unitsOfMeasure: this.meter.flowMeter.unitsOfMeasure.toLowerCase(),
                // eslint-disable-next-line no-underscore-dangle
                controller: this.meter?.controller?._id,
                probe,
            };
        }
        this.data.type = this?.flowmeterType || '';
        await this.getFlowMetersHubs();
        await this.getAvailableControllersHubs();
        await this.getAvailableProbes();
        this.componentInitialized = true;
        this.emitChange();
    },
    afterMounted() {
        this.getCustomName();
    },
    methods: {
        updateData() {
            if (!this.componentInitialized) return;
            if (this.matchSource(SOURCE_IOT) && this.data.flowMeter) { // IOT case
                const hub = this.availableMetersHubs.find(m => m.id === this.data.flowMeterHub);
                this.data.resetAt = hub.indirectChannelsDetails.find(m => m.id === this.data.flowMeter).resetAt;
            }

            if (this.data.resetAt && this.data.resetAt.length) {
                this.data.resetAt = parseFloat(this.data.resetAt);
            }
            this.emitChange();
            this.$emit('sendData', this.data);
        },

        async getAvailableProbes() {
            if (!this.data.controller) return;
            const selectedHub = this.availableControllersHubs.find(hub => hub.id === this.data.controller);
            if (selectedHub) {
                this.availableProbes = (selectedHub.probes || []).filter(detail => (this.editMode ? detail : detail.component === undefined));
            } else {
                this.availableProbes = [];
            }
        },
        async getFlowMetersHubs() {
            this.availableMetersHubs = await this.getAvailableFlowMetersHubs();
        },
        async getAvailableControllersHubs() {
            const availableControllersHubs = await this.$rq.getControllers(this.site.id);
            this.availableControllersHubs = availableControllersHubs.data.controllers;
        },
        setDataSource() {
            if (this.data.dataSource === SOURCE_CONTROLLER) this.data.dataSource = SOURCE_CONTROLLER;
            if (this.data.dataSource === SOURCE_IOT) this.data.dataSource = SOURCE_IOT;
            if (this.data.dataSource === SOURCE_MANUAL) this.data.dataSource = SOURCE_MANUAL;

            this.data.name = this.getCustomName();
        },

        matchSource(value) {
            return this.data.dataSource === value;
        },
        linkFlowMeterValidate() {
            const hasValidFields = field => this.data[field] && this.data[field].length > 0;
            const isValid = [
                'unitsOfMeasure',
            ].every(hasValidFields);
            if (!this.data.dataSource || !isValid) return false;
            switch (this.data.dataSource) {
                case SOURCE_MANUAL:
                    return hasValidFields('name');
                case SOURCE_CONTROLLER:
                    return this.data.controller && this.data.probe;
                default:
                    return false;
            }
        },
        emitChange() {
            this.$emit('inputChange', this.linkFlowMeterValidate);
        },
        getCustomName() {
            if (this.data.dataSource === SOURCE_CONTROLLER) {
                const probeId = this.data.probe;
                const probe = this.availableProbes.find(p => p.id === probeId);
                this.data.name = probe.probeId;
            }
            if (this.data.dataSource === SOURCE_MANUAL) {
                this.data.name = this.meter.flowMeter.name;
            } return '';
        },
    },
};
</script>
